<div cdkDrag cdkDragRootElement=".cdk-overlay-pane">
  <app-modal-header-ui
    cdkDragHandle
    i18n-title="@@new_deposit_account"
    title="New Deposit Account"
  ></app-modal-header-ui>

  <div class="dialog-body">
    <form class="deposit-account-form" [formGroup]="form">
      <!-- Type | Product | Purpose/Description | Currency (all three types) -->
      <div class="deposit-fields deposit-fields--identity">
        <mat-form-field class="type-field" appearance="outline" floatLabel="always">
          <mat-label i18n="@@type">Type</mat-label>
          <mat-select formControlName="productType" (selectionChange)="onProductTypeChange()">
            <mat-option *ngFor="let option of productTypeOptions" [value]="option.value">{{
              option.label
            }}</mat-option>
          </mat-select>
        </mat-form-field>

        <app-searchable-select
          class="product-field"
          label="Product"
          i18n-label="@@product"
          [options]="depositProducts"
          formControlName="savingsProductId"
        ></app-searchable-select>

        <mat-form-field class="purpose-field" appearance="outline" floatLabel="always">
          <mat-label i18n="@@purposeDescription">Purpose/Description</mat-label>
          <input type="text" formControlName="description" matInput />
        </mat-form-field>

        <div class="figma-field figma-field--disabled currency-field">
          <span class="figma-field-label" i18n="@@currency">Currency</span>
          <span>{{ currencyCode }}</span>
        </div>
      </div>

      <!-- ══════════════════ Deposit Account ══════════════════ -->
      <ng-container *ngIf="isDepositAccount">
        <div class="interest-overdraft-panel">
        <section class="design-section">
          <h3 class="design-section-heading">
            <span i18n="@@interest_expense">Interest Expense</span>
          </h3>

          <div class="deposit-fields deposit-fields--rates">
            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label i18n="@@minimum_balance">Minimum Balance</mat-label>
              <input
                type="tel"
                formControlName="averageBalance"
                matInput
                (focus)="onAmountFocus('averageBalance')"
                (blur)="onAmountBlur('averageBalance')"
              />
            </mat-form-field>

            <mat-form-field class="rate-field" appearance="outline" floatLabel="always">
              <mat-label i18n="@@interestRateLabel">Interest Rate</mat-label>
              <input type="tel" formControlName="interestRate" matInput />
              <span
                matSuffix
                class="unit-suffix"
                [class.is-locked]="form.get('interestRatePeriodUnit')?.disabled"
              >
                <mat-select formControlName="interestRatePeriodUnit">
                  <mat-select-trigger>
                    {{ rateUnitLabel(form.get("interestRatePeriodUnit")?.value) }}
                  </mat-select-trigger>
                  <mat-option *ngFor="let unit of periodUnitOptions" [value]="unit">
                    {{ rateUnitLabel(unit) }}
                  </mat-option>
                </mat-select>
              </span>
              <mat-hint *ngIf="interestRateHint">{{ interestRateHint }}</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label i18n="@@compounding">Compounding</mat-label>
              <mat-select formControlName="depositCompoundingPeriodUnit">
                <mat-option *ngFor="let unit of periodUnitOptions" [value]="unit">
                  {{ periodUnitLabel(unit) }}ly
                </mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label i18n="@@posting_period">Posting Period</mat-label>
              <mat-select formControlName="depositPostingPeriodUnit">
                <mat-option *ngFor="let unit of periodUnitOptions" [value]="unit">
                  {{ periodUnitLabel(unit) }}ly
                </mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label i18n="@@interest_calculation">Interest Calculation</mat-label>
              <mat-select formControlName="depositInterestCalculation">
                <mat-option value="AVERAGE_BALANCE">Average Balance</mat-option>
                <mat-option value="MINIMUM_BALANCE">Minimum Balance</mat-option>
                <mat-option value="DAILY_BALANCE">Daily Balance</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        </section>

        <section class="design-section">
          <h3 class="design-section-heading">
            <span i18n="@@overdraft">Overdraft</span>
            <label class="heading-toggle">
              <span i18n="@@allow_overdraft">Allow Overdraft</span>
              <mat-slide-toggle
                class="figma-toggle"
                formControlName="allowOverdraft"
                (change)="onAllowOverdraftChange()"
              ></mat-slide-toggle>
            </label>
          </h3>

          <div
            class="deposit-fields deposit-fields--rates"
            [class.fields-off]="!isSectionOn('allowOverdraft')"
          >
            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label i18n="@@overdraft_limit">Overdraft Limit</mat-label>
              <input
                type="tel"
                formControlName="overdraftLimit"
                matInput
                (focus)="onAmountFocus('overdraftLimit')"
                (blur)="onAmountBlur('overdraftLimit')"
              />
            </mat-form-field>

            <mat-form-field class="rate-field" appearance="outline" floatLabel="always">
              <mat-label i18n="@@interestRateLabel">Interest Rate</mat-label>
              <input type="tel" formControlName="overdraftInterestRate" matInput />
              <span
                matSuffix
                class="unit-suffix"
                [class.is-locked]="form.get('overdraftInterestRatePeriodUnit')?.disabled"
              >
                <mat-select formControlName="overdraftInterestRatePeriodUnit">
                  <mat-select-trigger>
                    {{ rateUnitLabel(form.get("overdraftInterestRatePeriodUnit")?.value) }}
                  </mat-select-trigger>
                  <mat-option *ngFor="let unit of periodUnitOptions" [value]="unit">
                    {{ rateUnitLabel(unit) }}
                  </mat-option>
                </mat-select>
              </span>
              <mat-hint *ngIf="overdraftInterestRateHint">
                {{ overdraftInterestRateHint }}
              </mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label i18n="@@compounding_frequency_label">Compounding Frequency</mat-label>
              <mat-select formControlName="overdraftCompoundingPeriodUnit">
                <mat-option *ngFor="let unit of periodUnitOptions" [value]="unit">
                  {{ periodUnitLabel(unit) }}ly
                </mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label i18n="@@posting_period">Posting Period</mat-label>
              <mat-select formControlName="overdraftPostingPeriodUnit">
                <mat-option *ngFor="let unit of periodUnitOptions" [value]="unit">
                  {{ periodUnitLabel(unit) }}ly
                </mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label i18n="@@interest_calculation">Interest Calculation</mat-label>
              <mat-select formControlName="overdraftInterestCalculation">
                <mat-option value="AVERAGE_BALANCE">Average Balance</mat-option>
                <mat-option value="MINIMUM_BALANCE">Minimum Balance</mat-option>
                <mat-option value="DAILY_BALANCE">Daily Balance</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        </section>
        </div>

        <section class="design-section">
          <button
            type="button"
            class="design-section-heading design-section-heading--button"
            (click)="otherInfoExpanded = !otherInfoExpanded"
            [attr.aria-expanded]="otherInfoExpanded"
          >
            <span i18n="@@other_deposit_product_information">
              Other Deposit Product Information
            </span>
            <mat-icon>{{ otherInfoExpanded ? "keyboard_arrow_down" : "chevron_right" }}</mat-icon>
          </button>

          <div class="other-info-card other-info-card--flush" *ngIf="otherInfoExpanded">
            <div class="deposit-fields deposit-fields--limits" *ngIf="isDepositAccount">
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label i18n="@@transfer_limit">Transfer Limit</mat-label>
                <input matInput type="tel" formControlName="transferLimitOverride" />
              </mat-form-field>
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label i18n="@@daily_transfer_limit">Daily Transfer Limit</mat-label>
                <input matInput type="tel" formControlName="dailyTransferLimitOverride" />
              </mat-form-field>
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label i18n="@@dormancy_threshold_days">Dormancy Threshold (Days)</mat-label>
                <input matInput appStrictNumbers type="tel" formControlName="dormancyThresholdOverride" />
              </mat-form-field>
            </div>

            <div class="product-reference-row">
              <mat-form-field appearance="outline" floatLabel="always" class="product-collection-field">
                <mat-label i18n="@@collection_accounts">Collection Account(s)</mat-label>
                <mat-select multiple formControlName="collectionAccountsOverride">
                  <mat-option *ngFor="let account of collectionAccountOptions" [value]="account.value">
                    {{ account.label }}
                  </mat-option>
                </mat-select>
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always" class="product-reference-select">
                <mat-label i18n="@@calculation_basis">Calculation Basis</mat-label>
                <mat-select formControlName="calculationBasisOverride">
                  <mat-option value="WORKING" i18n="@@working_days_option">Working Days</mat-option>
                  <mat-option value="CALENDAR" i18n="@@calender_days">Calendar Days</mat-option>
                </mat-select>
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always" class="product-weekends-field">
                <mat-label i18n="@@weekends">Weekends</mat-label>
                <mat-select multiple formControlName="weekendDaysOverride">
                  <mat-option *ngFor="let day of weekendDayOptions" [value]="day">
                    {{ readableValue(day) }}
                  </mat-option>
                </mat-select>
              </mat-form-field>

              <mat-form-field
                appearance="outline"
                floatLabel="always"
                class="product-rounding-field"
              >
                <mat-label i18n="@@rounding_multiple">Rounding Multiple</mat-label>
                <input matInput appStrictNumbers type="tel" formControlName="roundingMultipleOverride" />
              </mat-form-field>
            </div>

            <div class="account-channels-block">
              <h4 i18n="@@channels">Channels</h4>
              <div class="account-channels-row">
                <div class="account-channel" *ngFor="let channel of channelDisplays">
                  <img [src]="channel.icon" alt="" />
                  <span>{{ channel.label }}</span>
                  <mat-slide-toggle
                    class="figma-toggle channel-toggle"
                    [formControlName]="channel.control"
                  ></mat-slide-toggle>
                </div>
              </div>
            </div>

            <div class="fees-block">
              <h4 class="fees-heading" i18n="@@fees_and_charges">Fees &amp; Charges</h4>
              <ng-container *ngTemplateOutlet="feesGrid"></ng-container>
            </div>
          </div>
        </section>

        <section class="design-section calculator-section">
          <h3 class="design-section-heading">
            <span i18n="@@live_calculator">Live Calculator</span>
          </h3>

          <div class="calculator-strip">
            <label class="calc-field calc-field--input">
              <span class="calc-field-label calc-primary-input-label" i18n="@@average_balance">Average Balance</span>
              <input
                type="tel"
                formControlName="calcAverageBalance"
                (focus)="onAmountFocus('calcAverageBalance')"
                (blur)="onAmountBlur('calcAverageBalance')"
              />
            </label>

            <div class="calc-field">
              <span class="calc-field-label" i18n="@@interestRateLabel">Interest Rate</span>
              <span>{{ calculatorInterestRate }}</span>
            </div>

            <div class="calc-field">
              <span class="calc-field-label" i18n="@@compounding_frequency_label"
                >Compounding Frequency</span
              >
              <span>{{ compoundingLabel }}</span>
            </div>

            <div class="calc-field">
              <span class="calc-field-label" i18n="@@posting_period">Posting Period</span>
              <span>{{ postingPeriodLabel }}</span>
            </div>

            <div class="calc-field calc-field--projected">
              <span class="calc-field-label" i18n="@@projected_interest">Projected Interest</span>
              <span>{{ projectedInterest }}</span>
            </div>
          </div>
        </section>
      </ng-container>

      <!-- ══════════════════ Fixed Deposit ══════════════════ -->
      <ng-container *ngIf="isFixedDeposit">
        <div class="fixed-configuration-panel">
        <section class="design-section">
          <h3 class="design-section-heading">
            <span i18n="@@interest_expense">Interest Expense</span>
          </h3>

          <div class="deposit-fields deposit-fields--term">
            <mat-form-field class="amount-field" appearance="outline" floatLabel="always">
              <mat-label><ng-container i18n="@@amount">Amount</ng-container> *</mat-label>
              <input
                type="tel"
                formControlName="amount"
                matInput
                (focus)="onAmountFocus('amount')"
                (blur)="onAmountBlur('amount')"
              />
              <mat-hint *ngIf="amountHint">{{ amountHint }}</mat-hint>
            </mat-form-field>

            <mat-form-field class="duration-field" appearance="outline" floatLabel="always">
              <mat-label i18n="@@duration">Duration</mat-label>
              <input appStrictNumbers type="tel" formControlName="durationLength" matInput />
              <span
                matSuffix
                class="unit-suffix"
                [class.is-locked]="form.get('durationUnit')?.disabled"
              >
                <mat-select formControlName="durationUnit">
                  <mat-select-trigger>
                    {{ durationUnitLabel(form.get("durationUnit")?.value) }}
                  </mat-select-trigger>
                  <mat-option *ngFor="let unit of periodUnitOptions" [value]="unit">
                    {{ durationUnitLabel(unit) }}
                  </mat-option>
                </mat-select>
              </span>
              <mat-hint *ngIf="durationHint">{{ durationHint }}</mat-hint>
            </mat-form-field>

            <mat-form-field class="rate-field" appearance="outline" floatLabel="always">
              <mat-label i18n="@@interestRateLabel">Interest Rate</mat-label>
              <input type="tel" formControlName="interestRate" matInput />
              <span
                matSuffix
                class="unit-suffix"
                [class.is-locked]="form.get('interestRatePeriodUnit')?.disabled"
              >
                <mat-select formControlName="interestRatePeriodUnit">
                  <mat-select-trigger>
                    {{ rateUnitLabel(form.get("interestRatePeriodUnit")?.value) }}
                  </mat-select-trigger>
                  <mat-option *ngFor="let unit of periodUnitOptions" [value]="unit">
                    {{ rateUnitLabel(unit) }}
                  </mat-option>
                </mat-select>
              </span>
              <mat-hint *ngIf="interestRateHint">{{ interestRateHint }}</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label i18n="@@compounding_frequency_label">Compounding Frequency</mat-label>
              <mat-select formControlName="fixedDepCompoundingPeriodUnit">
                <mat-option *ngFor="let unit of periodUnitOptions" [value]="unit">
                  {{ periodUnitLabel(unit) }}ly
                </mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label i18n="@@interest_payout">Interest Payout</mat-label>
              <mat-select formControlName="fixedDepositInterestPayoutPeriodUnit">
                <mat-option *ngFor="let unit of payoutPeriodUnitOptions" [value]="unit">
                  {{ readableValue(unit) }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        </section>

        <div class="panel-pair">
          <section class="rule-panel">
            <h3 class="design-section-heading">
              <span i18n="@@maturity_instructions">Maturity Instructions</span>
            </h3>

            <label class="switch-row">
              <div>
                <span>
                  <ng-container i18n="@@auto_rollover_at_maturity"
                    >Auto-Rollover at Maturity</ng-container
                  >
                  <em class="req">*</em>
                </span>
                <p class="field-caption" i18n="@@noInstructionCaption">
                  If customer gives no instruction before maturity
                </p>
              </div>
              <mat-slide-toggle
                class="figma-toggle"
                formControlName="autoRollover"
              ></mat-slide-toggle>
            </label>

            <div
              class="deposit-fields deposit-fields--maturity"
              [class.fields-off]="!isSectionOn('autoRollover')"
            >
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label i18n="@@product">Product</mat-label>
                <mat-select formControlName="fixedDepositRolloverBasis">
                  <mat-option value="PRINCIPAL_ONLY" i18n="@@principalOnly">
                    Principal Only
                  </mat-option>
                  <mat-option value="PRINCIPAL_AND_INTEREST" i18n="@@principalPlusInterest">
                    Principal + Interest
                  </mat-option>
                </mat-select>
              </mat-form-field>
              <div class="captioned-field">
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label><ng-container i18n="@@grace_period">Grace Period</ng-container> *</mat-label>
                  <input
                    matInput
                    appStrictNumbers
                    type="tel"
                    formControlName="fixedDepositRolloverGracePeriodDays"
                  />
                </mat-form-field>
                <p class="field-caption" i18n="@@daysAfterMaturityCaption">
                  Days after maturity, before rollover
                </p>
              </div>
            </div>
          </section>

          <section class="rule-panel">
            <h3 class="design-section-heading">
              <span>
                <ng-container i18n="@@premature_withdrawal">Premature Withdrawal</ng-container>
                <em class="req">*</em>
              </span>
            </h3>

            <label class="switch-row">
              <div>
                <span>
                  <ng-container i18n="@@allow_premature_withdrawal"
                    >Allow Premature Withdrawal</ng-container
                  >
                  <em class="req">*</em>
                </span>
                <p class="field-caption" i18n="@@liquidateBeforeMaturityCaption">
                Customer can liquidate before maturity
                </p>
              </div>
              <mat-slide-toggle
                class="figma-toggle"
                formControlName="allowPrematureWithdrawal"
              ></mat-slide-toggle>
            </label>

            <div
              class="deposit-fields deposit-fields--withdrawal"
              [class.fields-off]="!isSectionOn('allowPrematureWithdrawal')"
            >
              <div class="captioned-field">
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>
                    <ng-container i18n="@@minimum_holding_period">Minimum Holding Period</ng-container> *
                  </mat-label>
                  <input
                    matInput
                    appStrictNumbers
                    type="tel"
                    formControlName="fixedDepositMinimumHoldingPeriodDays"
                  />
                </mat-form-field>
                <p class="field-caption" i18n="@@daysBeforeWithdrawalCaption">
                  Days before withdrawal is allowed
                </p>
              </div>

              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label i18n="@@penalty_on_interest">Penalty on Interest</mat-label>
                <input
                  matInput
                  type="number"
                  formControlName="fixedDepositPrematureWithdrawalPenaltyOnInterest"
                />
                <span matSuffix>%</span>
              </mat-form-field>

              <div class="captioned-field">
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label i18n="@@notice_period">Notice Period</mat-label>
                  <input
                    matInput
                    appStrictNumbers
                    type="tel"
                    formControlName="fixedDepositPrematureWithdrawalNoticePeriodDays"
                  />
                </mat-form-field>
                <p class="field-caption" i18n="@@daysNoticeCaption">Days' notice before payout</p>
              </div>
            </div>
          </section>
        </div>
        </div>

        <section class="design-section">
          <button
            type="button"
            class="design-section-heading design-section-heading--button"
            (click)="otherInfoExpanded = !otherInfoExpanded"
            [attr.aria-expanded]="otherInfoExpanded"
          >
            <span i18n="@@other_fixed_deposit_product_information">
              Other Fixed Deposit Product Information
            </span>
            <mat-icon>{{ otherInfoExpanded ? "keyboard_arrow_down" : "chevron_right" }}</mat-icon>
          </button>

          <div class="other-info-card other-info-card--flush" *ngIf="otherInfoExpanded">
            <div class="deposit-fields deposit-fields--limits" *ngIf="isDepositAccount">
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label i18n="@@transfer_limit">Transfer Limit</mat-label>
                <input matInput type="tel" formControlName="transferLimitOverride" />
              </mat-form-field>
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label i18n="@@daily_transfer_limit">Daily Transfer Limit</mat-label>
                <input matInput type="tel" formControlName="dailyTransferLimitOverride" />
              </mat-form-field>
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label i18n="@@dormancy_threshold_days">Dormancy Threshold (Days)</mat-label>
                <input matInput appStrictNumbers type="tel" formControlName="dormancyThresholdOverride" />
              </mat-form-field>
            </div>

            <div class="product-reference-row">
              <mat-form-field appearance="outline" floatLabel="always" class="product-collection-field">
                <mat-label i18n="@@collection_accounts">Collection Account(s)</mat-label>
                <mat-select multiple formControlName="collectionAccountsOverride">
                  <mat-option *ngFor="let account of collectionAccountOptions" [value]="account.value">
                    {{ account.label }}
                  </mat-option>
                </mat-select>
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always" class="product-reference-select">
                <mat-label i18n="@@calculation_basis">Calculation Basis</mat-label>
                <mat-select formControlName="calculationBasisOverride">
                  <mat-option value="WORKING" i18n="@@working_days_option">Working Days</mat-option>
                  <mat-option value="CALENDAR" i18n="@@calender_days">Calendar Days</mat-option>
                </mat-select>
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always" class="product-weekends-field">
                <mat-label i18n="@@weekends">Weekends</mat-label>
                <mat-select multiple formControlName="weekendDaysOverride">
                  <mat-option *ngFor="let day of weekendDayOptions" [value]="day">
                    {{ readableValue(day) }}
                  </mat-option>
                </mat-select>
              </mat-form-field>

              <mat-form-field
                appearance="outline"
                floatLabel="always"
                class="product-rounding-field"
              >
                <mat-label i18n="@@rounding_multiple">Rounding Multiple</mat-label>
                <input matInput appStrictNumbers type="tel" formControlName="roundingMultipleOverride" />
              </mat-form-field>
            </div>

            <div class="account-channels-block">
              <h4 i18n="@@channels">Channels</h4>
              <div class="account-channels-row">
                <div class="account-channel" *ngFor="let channel of channelDisplays">
                  <img [src]="channel.icon" alt="" />
                  <span>{{ channel.label }}</span>
                  <mat-slide-toggle
                    class="figma-toggle channel-toggle"
                    [formControlName]="channel.control"
                  ></mat-slide-toggle>
                </div>
              </div>
            </div>

            <div class="fees-block">
              <h4 class="fees-heading" i18n="@@fees_and_charges">Fees &amp; Charges</h4>
              <ng-container *ngTemplateOutlet="feesGrid"></ng-container>
            </div>
          </div>
        </section>

        <section class="design-section calculator-section">
          <h3 class="design-section-heading">
            <span i18n="@@live_calculator">Live Calculator</span>
          </h3>

          <div class="calculator-strip calculator-strip--fixed">
            <label class="calc-field calc-field--input">
              <span class="calc-field-label calc-primary-input-label" i18n="@@amount">Amount</span>
              <input
                type="tel"
                formControlName="calcFixedAmount"
                (focus)="onAmountFocus('calcFixedAmount')"
                (blur)="onAmountBlur('calcFixedAmount')"
              />
            </label>

            <div class="calc-field">
              <span class="calc-field-label" i18n="@@durationDaysLabel">Duration (days)</span>
              <span>{{ durationDisplay }}</span>
            </div>

            <div class="calc-field">
              <span class="calc-field-label" i18n="@@interestRateLabel">Interest Rate</span>
              <span>{{ calculatorInterestRate }}</span>
            </div>

            <div class="calc-field">
              <span class="calc-field-label" i18n="@@interest_to_maturity"
                >Interest to Maturity</span
              >
              <span>{{ interestToMaturity }}</span>
            </div>

            <div class="calc-field">
              <span class="calc-field-label" i18n="@@maturity_value">Maturity Value</span>
              <span>{{ maturityValue }}</span>
            </div>
          </div>
        </section>
      </ng-container>

      <!-- ══════════════════ Recurring / Targeted Savings ══════════════════ -->
      <ng-container *ngIf="isRecurringSavings">
        <div class="recurring-interest-panel">
        <section class="design-section">
          <h3 class="design-section-heading">
            <span i18n="@@interest_expense">Interest Expense</span>
          </h3>

          <div class="deposit-fields deposit-fields--term">
            <mat-form-field class="amount-field" appearance="outline" floatLabel="always">
              <mat-label
                ><ng-container i18n="@@target_amount">Target Amount</ng-container> *</mat-label
              >
              <input
                type="tel"
                formControlName="targetAmount"
                matInput
                (focus)="onAmountFocus('targetAmount')"
                (blur)="onAmountBlur('targetAmount')"
              />
              <mat-hint *ngIf="amountHint">{{ amountHint }}</mat-hint>
            </mat-form-field>

            <mat-form-field class="duration-field" appearance="outline" floatLabel="always">
              <mat-label i18n="@@duration">Duration</mat-label>
              <input
                appStrictNumbers
                type="tel"
                formControlName="recurringDurationLength"
                matInput
              />
              <span
                matSuffix
                class="unit-suffix"
                [class.is-locked]="form.get('recurringDurationUnit')?.disabled"
              >
                <mat-select formControlName="recurringDurationUnit">
                  <mat-select-trigger>
                    {{ durationUnitLabel(form.get("recurringDurationUnit")?.value) }}
                  </mat-select-trigger>
                  <mat-option *ngFor="let unit of periodUnitOptions" [value]="unit">
                    {{ durationUnitLabel(unit) }}
                  </mat-option>
                </mat-select>
              </span>
              <mat-hint *ngIf="durationHint">{{ durationHint }}</mat-hint>
            </mat-form-field>

            <mat-form-field class="rate-field" appearance="outline" floatLabel="always">
              <mat-label i18n="@@interestRateLabel">Interest Rate</mat-label>
              <input type="tel" formControlName="interestRate" matInput />
              <span
                matSuffix
                class="unit-suffix"
                [class.is-locked]="form.get('interestRatePeriodUnit')?.disabled"
              >
                <mat-select formControlName="interestRatePeriodUnit">
                  <mat-select-trigger>
                    {{ rateUnitLabel(form.get("interestRatePeriodUnit")?.value) }}
                  </mat-select-trigger>
                  <mat-option *ngFor="let unit of periodUnitOptions" [value]="unit">
                    {{ rateUnitLabel(unit) }}
                  </mat-option>
                </mat-select>
              </span>
              <mat-hint *ngIf="interestRateHint">{{ interestRateHint }}</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label i18n="@@compounding_frequency_label">Compounding Frequency</mat-label>
              <mat-select formControlName="recurringCompoundingPeriodUnit">
                <mat-option *ngFor="let unit of periodUnitOptions" [value]="unit">
                  {{ periodUnitLabel(unit) }}ly
                </mat-option>
              </mat-select>
            </mat-form-field>

            <div class="figma-field figma-field--disabled">
              <span class="figma-field-label" i18n="@@interest_payout">Interest Payout</span>
              <span>{{ interestPayoutLabel }}</span>
              <mat-icon aria-hidden="true">expand_more</mat-icon>
            </div>
          </div>
        </section>

        <section class="design-section">
          <h3 class="design-section-heading">
            <span i18n="@@interest_expense">Interest Expense</span>
          </h3>

          <div class="deposit-fields deposit-fields--schedule">
            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label i18n="@@savings_frequency">Savings Frequency</mat-label>
              <mat-select formControlName="recurringFrequencyUnit">
                <mat-option *ngFor="let unit of periodUnitOptions" [value]="unit">
                  {{ periodUnitLabel(unit) }}ly
                </mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label i18n="@@day_of_month">Day of Month</mat-label>
              <input appStrictNumbers type="tel" formControlName="recurringDayOfMonth" matInput />
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label i18n="@@contribution_source">Contribution Source</mat-label>
              <mat-select formControlName="recurringContributionSource">
                <mat-option value="AUTO_DEBIT_LINKED_ACCOUNT">Auto Debit Linked Account</mat-option>
                <mat-option value="MANUAL_DEPOSIT">Manual Deposit</mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label i18n="@@grace_period">Grace Period</mat-label>
              <input appStrictNumbers type="tel" formControlName="recurringGracePeriod" matInput />
            </mat-form-field>

            <!-- Award a Bonus on Completion: one label over a split control -->
            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label i18n="@@award_bonus_on_completion">Award a Bonus on Completion</mat-label>
              <input type="tel" formControlName="recurringBonus" matInput />
            </mat-form-field>
          </div>
        </section>
        </div>

        <div class="recurring-body">
          <div class="rule-stack">
            <section class="rule-block rule-block--withdrawal">
              <h4 class="rule-block-heading rule-block-heading--accent">
                <span i18n="@@allow_early_withdrawal">Allow Early Withdrawal</span>
                <mat-slide-toggle
                  class="figma-toggle"
                  formControlName="allowEarlyWithdrawal"
                ></mat-slide-toggle>
              </h4>

              <!-- Editable, ported from new-view-deposit-product's
                   .early-withdrawal-* block (its backgrounds and paddings are
                   left behind; this modal has its own). Layout still follows
                   Figma 15913:24678 — lock period left, penalty right, 20px
                   apart, the two penalty controls butted into one box. -->
              <div
                class="early-withdrawal-body"
                [class.fields-off]="!isSectionOn('allowEarlyWithdrawal')"
              >
                <div class="early-lock-period">
                  <span class="early-lock-copy">
                    <span i18n="@@minimum_lock_period">Minimum Lock Period</span>
                    <small i18n="@@daysFromFirstContribution"
                      >Days from first contribution</small
                    >
                  </span>
                  <mat-form-field
                    appearance="outline"
                    class="early-control-field early-lock-field"
                  >
                    <input
                      matInput
                      appStrictNumbers
                      formControlName="minimumLockPeriodDays"
                    />
                  </mat-form-field>
                </div>

                <div class="early-penalty-field">
                  <span
                    class="early-penalty-label"
                    i18n="@@penalty_on_accrued_interest"
                    >Penalty on Accrued Interest</span
                  >
                  <mat-form-field
                    appearance="outline"
                    class="early-control-field early-penalty-type"
                  >
                    <mat-select formControlName="earlyWithdrawalPenaltyType">
                      <mat-option value="PERCENTAGE" i18n="@@percentage"
                        >Percentage</mat-option
                      >
                      <mat-option value="FLAT" i18n="@@flat_amount"
                        >Flat Amount</mat-option
                      >
                    </mat-select>
                  </mat-form-field>
                  <mat-form-field
                    appearance="outline"
                    class="percentage-field early-control-field early-penalty-value"
                  >
                    <input matInput formControlName="earlyWithdrawalPenaltyValue" />
                    <span
                      *ngIf="
                        form.get('earlyWithdrawalPenaltyType').value ===
                        'PERCENTAGE'
                      "
                      class="percentage-symbol"
                      >%</span
                    >
                  </mat-form-field>
                </div>
              </div>
            </section>

            <section class="rule-block rule-block--missed">
              <h4 class="rule-block-heading rule-block-heading--accent">
                <span i18n="@@missed_contribution">Missed Contribution</span>
              </h4>

              <div class="rule-rows">
                <div class="inline-row">
                  <span
                    class="inline-label inline-label--auto"
                    i18n="@@penalty_per_missed_contribution"
                  >
                    Penalty per Missed Contribution
                  </span>
                  <mat-form-field
                    appearance="outline"
                    class="inline-field inline-field--type"
                  >
                    <mat-select formControlName="missedContributionPenaltyType">
                      <mat-option value="PERCENTAGE" i18n="@@percentage"
                        >Percentage</mat-option
                      >
                      <mat-option value="FLAT" i18n="@@flat_amount"
                        >Flat Amount</mat-option
                      >
                    </mat-select>
                  </mat-form-field>
                  <mat-form-field
                    appearance="outline"
                    class="inline-field inline-field--value"
                  >
                    <input
                      matInput
                      type="tel"
                      formControlName="missedContributionPenaltyValue"
                    />
                    <span
                      matSuffix
                      *ngIf="form.get('missedContributionPenaltyType')?.value === 'PERCENTAGE'"
                      >%</span
                    >
                  </mat-form-field>
                </div>

                <label class="inline-row inline-row--switch">
                  <span
                    class="inline-label inline-label--auto"
                    i18n="@@forfeitBonusMissedContribution"
                  >
                    Forfeit bonus on completion after any missed contribution
                  </span>
                  <mat-slide-toggle
                    class="figma-toggle"
                    formControlName="forfeitBonusOnMissedContribution"
                  ></mat-slide-toggle>
                </label>
              </div>
            </section>

            <section class="rule-block rule-block--completion">
              <h4 class="rule-block-heading">
                <span i18n="@@at_completion">At Completion</span>
              </h4>
              <mat-form-field appearance="outline" floatLabel="always" class="inline-field inline-field--full">
                <mat-label i18n="@@at_completion">At Completion</mat-label>
                <mat-select formControlName="recurringAtCompletion">
                  <mat-option value="TRANSFER_TO_LINKED_SAVINGS_ACCOUNT">Transfer to Linked Savings Account</mat-option>
                  <mat-option value="CASH_PAYOUT_OR_WITHDRAWAL">Cash Payout or Withdrawal</mat-option>
                </mat-select>
              </mat-form-field>
            </section>
          </div>

          <section class="calc-panel">
            <h3 class="calc-panel-heading" i18n="@@live_calculator">Live Calculator</h3>

            <label class="calc-panel-row">
              <span>
                <ng-container i18n="@@target_amount">Target Amount</ng-container>
                <em class="req">*</em>
              </span>
              <input
                class="calc-panel-input"
                type="tel"
                formControlName="calcTargetAmount"
                (focus)="onAmountFocus('calcTargetAmount')"
                (blur)="onAmountBlur('calcTargetAmount')"
              />
            </label>

            <label class="calc-panel-row">
              <span>
                <ng-container i18n="@@durationMonthsLabel">Duration (months)</ng-container>
                <em class="req">*</em>
              </span>
              <input
                class="calc-panel-input"
                appStrictNumbers
                type="tel"
                formControlName="calcDurationMonths"
              />
            </label>

            <label class="calc-panel-row">
              <span>
                <ng-container i18n="@@interestRatePerMonthLabel"
                  >Interest Rate (per month)</ng-container
                >
                <em class="req">*</em>
              </span>
              <input class="calc-panel-input" type="tel" formControlName="calcInterestRate" />
            </label>

            <div class="calc-panel-row">
              <span i18n="@@contribution_per_period">Contribution per Period</span>
              <output class="calc-panel-output">{{ contributionPerPeriod }}</output>
            </div>

            <!-- Figma 15913:24716 groups the note with this row, 1px under it. -->
            <div class="calc-panel-group">
              <div class="calc-panel-row">
                <span i18n="@@projected_interest_earned">Projected Interest Earned</span>
                <output class="calc-panel-output">{{ projectedInterestEarned }}</output>
              </div>

              <p class="calc-panel-note" i18n="@@interestApproximationNote">
                Approximated; actual interest accrual depends on exact contribution dates.
              </p>
            </div>

            <div class="calc-panel-row calc-panel-row--total">
              <span i18n="@@projected_balance_at_completion">
                Projected Balance at Completion
              </span>
              <output class="calc-panel-output">{{ projectedBalanceAtCompletion }}</output>
            </div>
          </section>
        </div>

        <section class="design-section">
          <button
            type="button"
            class="design-section-heading design-section-heading--button"
            (click)="otherInfoExpanded = !otherInfoExpanded"
            [attr.aria-expanded]="otherInfoExpanded"
          >
            <span i18n="@@other_deposit_product_information">
              Other Deposit Product Information
            </span>
            <mat-icon>{{ otherInfoExpanded ? "keyboard_arrow_down" : "chevron_right" }}</mat-icon>
          </button>

          <div class="other-info-card other-info-card--flush" *ngIf="otherInfoExpanded">
            <div class="deposit-fields deposit-fields--limits" *ngIf="isDepositAccount">
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label i18n="@@transfer_limit">Transfer Limit</mat-label>
                <input matInput type="tel" formControlName="transferLimitOverride" />
              </mat-form-field>
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label i18n="@@daily_transfer_limit">Daily Transfer Limit</mat-label>
                <input matInput type="tel" formControlName="dailyTransferLimitOverride" />
              </mat-form-field>
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label i18n="@@dormancy_threshold_days">Dormancy Threshold (Days)</mat-label>
                <input matInput appStrictNumbers type="tel" formControlName="dormancyThresholdOverride" />
              </mat-form-field>
            </div>

            <div class="product-reference-row">
              <mat-form-field appearance="outline" floatLabel="always" class="product-collection-field">
                <mat-label i18n="@@collection_accounts">Collection Account(s)</mat-label>
                <mat-select multiple formControlName="collectionAccountsOverride">
                  <mat-option *ngFor="let account of collectionAccountOptions" [value]="account.value">
                    {{ account.label }}
                  </mat-option>
                </mat-select>
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always" class="product-reference-select">
                <mat-label i18n="@@calculation_basis">Calculation Basis</mat-label>
                <mat-select formControlName="calculationBasisOverride">
                  <mat-option value="WORKING" i18n="@@working_days_option">Working Days</mat-option>
                  <mat-option value="CALENDAR" i18n="@@calender_days">Calendar Days</mat-option>
                </mat-select>
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always" class="product-weekends-field">
                <mat-label i18n="@@weekends">Weekends</mat-label>
                <mat-select multiple formControlName="weekendDaysOverride">
                  <mat-option *ngFor="let day of weekendDayOptions" [value]="day">
                    {{ readableValue(day) }}
                  </mat-option>
                </mat-select>
              </mat-form-field>

              <mat-form-field
                appearance="outline"
                floatLabel="always"
                class="product-rounding-field"
              >
                <mat-label i18n="@@rounding_multiple">Rounding Multiple</mat-label>
                <input matInput appStrictNumbers type="tel" formControlName="roundingMultipleOverride" />
              </mat-form-field>
            </div>

            <div class="account-channels-block">
              <h4 i18n="@@channels">Channels</h4>
              <div class="account-channels-row">
                <div class="account-channel" *ngFor="let channel of channelDisplays">
                  <img [src]="channel.icon" alt="" />
                  <span>{{ channel.label }}</span>
                  <mat-slide-toggle
                    class="figma-toggle channel-toggle"
                    [formControlName]="channel.control"
                  ></mat-slide-toggle>
                </div>
              </div>
            </div>

            <div class="fees-block">
              <h4 class="fees-heading" i18n="@@fees_and_charges">Fees &amp; Charges</h4>
              <ng-container *ngTemplateOutlet="feesGrid"></ng-container>
            </div>
          </div>
        </section>
      </ng-container>

      <!-- Actions (all three types) -->
      <div class="deposit-actions">
        <app-button-icon-ui
          [buttonType]="'button'"
          classNames="deposit-btn deposit-btn--cancel"
          (clickEmit)="cancel()"
          i18n-name="@@cancel"
          name="Cancel"
          value="cancel"
          tooltip="Cancel"
          i18n-tooltip="@@cancelTooltip"
        ></app-button-icon-ui>
        <app-button-icon-ui
          [isDisabled]="form.invalid"
          [buttonType]="'button'"
          classNames="deposit-btn deposit-btn--next"
          [icon]="'chevron_right'"
          [iconPosition]="'right'"
          i18n-name="@@next"
          name="Next"
          value="submit"
          tooltip="Submit"
          i18n-tooltip="@@submitTooltip"
          (clickEmit)="onSubmit()"
        ></app-button-icon-ui>
      </div>
    </form>
  </div>
</div>

<!-- Fixed Deposit and Recurring Savings carry Fees & Charges as its own
     collapsible section; Deposit Account nests the grid in its grey card. -->
<ng-template #feesSection>
  <section class="design-section">
    <button
      type="button"
      class="design-section-heading design-section-heading--button"
      (click)="feesExpanded = !feesExpanded"
      [attr.aria-expanded]="feesExpanded"
    >
      <span i18n="@@fees_and_charges">Fees &amp; Charges</span>
      <mat-icon>{{ feesExpanded ? "keyboard_arrow_down" : "chevron_right" }}</mat-icon>
    </button>

    <div class="other-info-card" *ngIf="feesExpanded">
      <div class="fees-block fees-block--standalone">
        <ng-container *ngTemplateOutlet="feesGrid"></ng-container>
      </div>
    </div>
  </section>
</ng-template>

<ng-template #feesGrid>
  <div class="fees-grid-scroll">
    <div class="fees-grid">
      <div class="fees-grid-row fees-grid-header">
        <span i18n="@@fee_charge_name">Fee/Charge Name</span>
        <span i18n="@@value">Value</span>
        <span i18n="@@fee_minimum">Minimum</span>
        <span i18n="@@fee_maximum">Maximum</span>
        <span i18n="@@event">Event</span>
      </div>

      <div class="fees-grid-row fees-grid-data" *ngFor="let fee of productFees">
        <span class="fee-control fee-name">{{ fee.name }}</span>
        <span class="fee-control fee-amount">{{ fee.value }}</span>
        <span class="fee-control fee-amount">{{ fee.minimum }}</span>
        <span class="fee-control fee-amount">{{ fee.maximum }}</span>

        <span class="fee-event fee-event--daily" *ngIf="fee.eventType !== 'QUARTERLY'">
          <span>{{ fee.event }}</span>
          <span>{{ fee.eventTime }}</span>
        </span>

        <span class="fee-event fee-event--quarterly" *ngIf="fee.eventType === 'QUARTERLY'">
          <span class="fee-quarterly-label">{{ fee.event }}</span>
          <span class="fee-quarter"><small>Q1</small>{{ fee.quarters[0] }}</span>
          <span class="fee-quarter"><small>Q2</small>{{ fee.quarters[1] }}</span>
          <span class="fee-quarter"><small>Q3</small>{{ fee.quarters[2] }}</span>
          <span class="fee-quarter"><small>Q4</small>{{ fee.quarters[3] }}</span>
        </span>
      </div>

      <div class="fees-empty" *ngIf="!productFees.length" i18n="@@noFeesConfigured">
        No fees or charges configured
      </div>
    </div>
  </div>
</ng-template>
